import { Key } from 'react';

import type { Pagination } from 'models/pagination';
import type { UserTracker, User } from 'models/user';
import { getQueryArray } from 'utils/helpers/getQueryArray';

import { api, CACHE_TTL } from '..';
import type { AddUser, FetchUsers, EditUser, UploadAvatar, EditPermissions, IEditTracker } from './users';

export const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    fetchUsers: build.query<{ data: User[]; pagination: Pagination }, FetchUsers>({
      query: ({ roles, statuses, ...params }) => {
        if (params.search) {
          params.page = params.page || 1;
        }

        const parsedRoles = getQueryArray(roles, 'roles');
        const parsedStatuses = statuses
          ? getQueryArray(statuses, 'statuses')
          : getQueryArray(['ACTIVE'], 'statuses');

        return {
          url: `/users?${parsedRoles}&${parsedStatuses}`,
          params,
        };
      },
      providesTags: ['Users'],
      keepUnusedDataFor: CACHE_TTL / 2,
    }),
    addUser: build.mutation<User, AddUser>({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: { data: User }) => {
        return rawResult.data;
      },
      invalidatesTags: ['Users'],
    }),
    updateUser: build.mutation<User, EditUser>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (rawResult: { data: User }) => {
        return rawResult.data;
      },
      invalidatesTags: ['Users'],
    }),
    updateUserPermissions: build.mutation<User, EditPermissions>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}/permissions`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Users', 'User'],
    }),
    addUserTrackers: build.mutation<User, UserTracker>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}/trackers`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    fetchUserTrackers: build.query<UserTracker, Key>({
      query: (id) => `/users/${id}/trackers`,
      transformResponse: (rawResult: { data: UserTracker }) => {
        return rawResult.data;
      },
      providesTags: ['Users'],
    }),
    deleteTracker: build.mutation<void, any>({
      query: (data) => ({
        url: `/users/${data.id}/trackers/${data.trackerID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Calendar', 'Users'],
    }),
    updateUserTracker: build.mutation<User, IEditTracker>({
      query: ({ id, trackerID, token, permissions }) => ({
        url: `/users/${id}/trackers/${trackerID}`,
        method: 'PATCH',
        body: {
          token,
          permissions,
        },
      }),
      invalidatesTags: ['Users'],
    }),
    fetchOneUser: build.query<User, Key>({
      query: (id) => `/users/${id}`,
      transformResponse: (rawResult: { data: User }) => {
        return rawResult.data;
      },
      providesTags: ['Users'],
    }),
    fetchMe: build.query<User, void>({
      query: () => '/me',
      transformResponse: (rawResult: { data: User }) => {
        return rawResult.data;
      },
      providesTags: ['User'],
    }),
    uploadAvatar: build.mutation<void, UploadAvatar>({
      query: ({ userId, avatar }) => {
        const formData = new FormData();
        formData.append('avatar', avatar);
        return {
          url: `/users/${userId}/avatar`,
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['Users'],
    }),
  }),
});
